import { Controller } from "@hotwired/stimulus";

// Builds a single circular play/pause button backed by a hidden <audio> element.
// The surrounding section template is sanitized server-side (no <script>, no
// <button> tag), so the interactive markup is created here in JS after the
// sanitize pass — mirroring the video-embed controller. The audio URL and its
// MIME type arrive as allowlisted data-* values set by the template.
export default class extends Controller {
  static values = { url: String, mimeType: String };

  connect() {
    if (!this.urlValue) {
      console.warn("audio-player: no audio URL provided.");
      return;
    }
    this.buildPlayer();
  }

  disconnect() {
    // Stop playback if the element is torn down (e.g. Turbo navigation) so audio
    // never outlives its button.
    this.audio?.pause();
  }

  buildPlayer() {
    this.audio = document.createElement("audio");
    this.audio.preload = "none";
    const source = document.createElement("source");
    source.src = this.urlValue;
    if (this.mimeTypeValue) source.type = this.mimeTypeValue;
    this.audio.appendChild(source);
    this.audio.addEventListener("ended", () => this.reset());

    this.button = document.createElement("button");
    this.button.type = "button";
    this.button.className = "player__button";
    this.button.innerHTML = this.playIcon;
    this.setLabel("Play");
    this.button.addEventListener("click", () => this.toggle());

    this.element.replaceChildren(this.button, this.audio);
  }

  toggle() {
    if (this.audio.paused) {
      // Flip to the Pause state only once playback actually starts; if play()
      // is rejected (e.g. autoplay policy or a decode error) reset() leaves the
      // button on Play, so the icon never lies about whether sound is playing.
      this.audio
        .play()
        .then(() => {
          this.button.innerHTML = this.pauseIcon;
          this.setLabel("Pause");
        })
        .catch(() => this.reset());
    } else {
      this.audio.pause();
      this.reset();
    }
  }

  reset() {
    this.button.innerHTML = this.playIcon;
    this.setLabel("Play");
  }

  setLabel(label) {
    this.button.setAttribute("aria-label", label);
    this.button.setAttribute("title", label);
  }

  get playIcon() {
    // Bootstrap Icons "play-fill" (MIT). currentColor so SCSS controls the colour.
    return `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true"><path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393"/></svg>`;
  }

  get pauseIcon() {
    // Bootstrap Icons "pause-fill" (MIT).
    return `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true"><path d="M5.5 3.5A1.5 1.5 0 0 1 7 5v6a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 1.5-1.5m5 0A1.5 1.5 0 0 1 12 5v6a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 1.5-1.5"/></svg>`;
  }
}
